"use client";

import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { motion } from "framer-motion";
import { getEmployees } from "../services/employeesApi";
import { Employee } from "../types/employee";
import { addQuickPayment } from "../services/BookingsApi";
import { useAuth } from "../auth/AuthContext";
import PaymentCompletedModal from "./PaymentCompletedModal";

interface Stylist {
  id: number;
  initials: string;
  name: string;
  lightColor: string;
  darkColor: string;
}

// Color scheme for employee avatars
const colorSchemes = [
  { light: "bg-[#E7E2F3]", dark: "bg-[#7E57C2]" },
  { light: "bg-[#F7EAF4]", dark: "bg-[#C2185B]" },
  { light: "bg-[#DDF4ED]", dark: "bg-[#29BF6A]" },
  { light: "bg-[#F9ECF8]", dark: "bg-[#EC4899]" },
];

// Generate initials from employee name
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Map employee to stylist format
const mapEmployeeToStylist = (employee: Employee, index: number): Stylist => {
  const colorScheme = colorSchemes[index % colorSchemes.length];
  return {
    id: employee.id,
    initials: getInitials(employee.name),
    name: employee.name,
    lightColor: colorScheme.light,
    darkColor: colorScheme.dark,
  };
};

export default function QuickPaymentPanel({
  onClose,
  onProceed,
  onPaymentComplete,
  quickPaymentType = "stylist",
}: {
  onClose: () => void;
  onProceed: () => void;
  onPaymentComplete?: (bookingId: number | string) => void;
  quickPaymentType?: "direct" | "stylist";
}) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [amount, setAmount] = useState("0.00");
  const [hasDecimal, setHasDecimal] = useState(false);
  const [decimalDigits, setDecimalDigits] = useState(0);
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [completedBookingId, setCompletedBookingId] = useState<number | string | null>(null);
  const [lastPaymentType, setLastPaymentType] = useState<string>("cash");
  const { user } = useAuth();

  // Check if XE Connect is active
  const isPayWithXEConnect = user?.company?.pay_with_xeconnect === "active";

  // Fetch employees when in stylist mode
  useEffect(() => {
    if (quickPaymentType === "stylist") {
      const fetchEmployees = async () => {
        setLoading(true);
        try {
          const employees = await getEmployees();
          // Filter out employees with "Unnamed" and map to stylists
          const mappedStylists = employees
            .filter((emp) => emp.name && emp.name !== "Unnamed")
            .map((emp, index) => mapEmployeeToStylist(emp, index));
          setStylists(mappedStylists);
          // Auto-select first employee if available and none selected
          if (mappedStylists.length > 0) {
            setSelectedId((prev) => (prev === null ? mappedStylists[0].id : prev));
          }
        } catch (error) {
          console.error("Error fetching employees:", error);
          setStylists([]);
        } finally {
          setLoading(false);
        }
      };
      fetchEmployees();
    } else {
      // Clear stylists when not in stylist mode
      setStylists([]);
      setSelectedId(null);
    }
  }, [quickPaymentType]);

  const filtered = stylists.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  // XE Connect payment functions
  const checkXEServerUp = async (): Promise<boolean> => {
    try {
      const response = await fetch("http://localhost:5050/api/Xepos/IsServerUp", {
        method: "GET",
      });
      return response.ok;
    } catch (error) {
      console.error("XE Connect server check failed:", error);
      return false;
    }
  };

  const processXEPayment = async (totalAmount: number): Promise<{ approved: boolean; transactionId?: string }> => {
    // Generate random text for OrderNo
    const randomText = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    try {
      // Check if server is up
      const isServerUp = await checkXEServerUp();
      if (!isServerUp) {
        alert("Please first run the Xe-Connect server!");
        return { approved: false };
      }

      // Process payment - send as Form Data
      // Format amount: multiply by 100 and convert to string (as per original logic)
      const formattedAmount = Math.round(totalAmount * 100).toString();
      const formData = new URLSearchParams();
      formData.append("Amount", formattedAmount);
      formData.append("OrderNo", randomText);

      const response = await fetch("http://localhost:5050/api/Xepos/Evo/Websale", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      const data = await response.json();

      if (data.StatusMessage === "Approved") {
        setSuccessMessage("Card payment is done successfully!");
        return { approved: true, transactionId: data.TransactionId };
      } else {
        alert("Card payment is cancelled/declined!");
        return { approved: false };
      }
    } catch (error) {
      console.error("XE Connect payment error:", error);
      alert("Something went wrong, please try again later!");
      return { approved: false };
    }
  };

  // Handle payment submission
  const handlePayment = async (paymentType: "cash" | "card") => {
    const amountValue = parseFloat(amount);

    if (amountValue <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    setSubmitting(true);
    setSuccessMessage(null);
    setLastPaymentType(paymentType);

    try {
      let transactionId: string | null = null;

      // Check for card payment with XE Connect
      if (paymentType === "card" && isPayWithXEConnect) {
        setShowLoadingOverlay(true);
        try {
          const xeResult = await processXEPayment(amountValue);
          setShowLoadingOverlay(false);

          if (!xeResult.approved) {
            setSubmitting(false);
            return;
          }

          transactionId = xeResult.transactionId || null;
        } catch (error) {
          setShowLoadingOverlay(false);
          setSubmitting(false);
          return;
        }
      }

      // Fetch tax settings to calculate net amount (backend expects net as per user request)
      let taxPercent = 0;
      try {
        const settings = await import("../services/settings").then(m => m.getSettings());
        taxPercent = settings?.data?.tax?.percent || 0;
      } catch (err) {
        console.warn("Failed to fetch tax settings for quick payment, using 0% tax.", err);
      }

      const netToGrossRatio = 1 / (1 + taxPercent / 100);
      const netAmount = amountValue * netToGrossRatio;

      // Make API call to store payment
      const payload = {
        emp_id: quickPaymentType === "stylist" && selectedId ? selectedId : null,
        amount: netAmount,
        payment_type: paymentType,
        trans_id: transactionId || null,
      };

      const response = await addQuickPayment(payload);

      if (response.status === "success") {
        setApiResponse(response);
        setCompletedBookingId((response as any).booking?.id || (response as any).id);

        // Show success modal after a tiny delay for visual confirmation of processing
        setTimeout(() => {
          setSubmitting(false);
          setShowSuccessModal(true);
        }, 300);
      } else {
        alert(response.message || "Failed to add payment");
      }
    } catch (error: any) {
      console.error("Error adding quick payment:", error);
      alert(error.response?.data?.message || "Failed to add payment. Please try again.");
    } finally {
      setSubmitting(false);
      setShowLoadingOverlay(false);
    }
  };

  const handleKey = (key: string) => {
    setAmount((prev) => {
      let [intPart, decPart] = prev.split(".");
      decPart = decPart || "00";

      // DELETE key
      if (key === "del") {
        if (hasDecimal && decimalDigits > 0) {
          // delete decimal digits first
          const newDec =
            decPart.slice(0, decimalDigits - 1) +
            "0".repeat(2 - (decimalDigits - 1));
          setDecimalDigits(decimalDigits - 1);
          if (decimalDigits - 1 === 0) setHasDecimal(false);
          return `${intPart}.${newDec}`;
        } else if (intPart.length > 1) {
          intPart = intPart.slice(0, -1);
          return `${parseInt(intPart)}.00`;
        } else {
          setHasDecimal(false);
          setDecimalDigits(0);
          return "0.00";
        }
      }

      // DECIMAL key
      if (key === ".") {
        if (hasDecimal) return prev;
        setHasDecimal(true);
        setDecimalDigits(0);
        return `${intPart}.00`;
      }

      // NUMBER keys (0-9 and 00)
      if (/^\d+$/.test(key)) {
        if (!hasDecimal) {
          // building integer part
          if (intPart === "0") {
            if (key === "0" || key === "00") return "0.00";
            intPart = key;
          } else {
            intPart += key;
          }
          // Cap integer length
          if (intPart.length > 9) return prev;
          return `${intPart}.00`;
        } else {
          // building decimal part (max 2 digits)
          if (decimalDigits >= 2) return prev;

          if (key === "00") {
            if (decimalDigits === 0) {
              setDecimalDigits(2);
              return `${intPart}.00`;
            } else if (decimalDigits === 1) {
              setDecimalDigits(2);
              return `${intPart}.${decPart[0]}0`;
            }
            return prev;
          }

          const newDec = (decPart.slice(0, decimalDigits) + key).slice(0, 2).padEnd(2, "0");
          setDecimalDigits(decimalDigits + 1);
          return `${intPart}.${newDec}`;
        }
      }

      return prev;
    });
  };
  // -------------------------------

  return (
    <div className="fixed inset-0 flex justify-end z-[100]">
      {/* Loading Overlay for XE Connect */}
      {showLoadingOverlay && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#885ABB]"></div>
            <p className="text-gray-700 font-medium">Processing card payment...</p>
          </div>
        </div>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose}></div>

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[60] animate-fade-in">
          <div className="bg-[#22C55E] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-md">
            <svg
              className="w-6 h-6 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Sliding Panel */}
      {/* Sliding Panel */}
      <div className={`relative bg-white h-full flex flex-col lg:flex-row transform transition-transform duration-300 ease-out translate-x-0 ${quickPaymentType === "direct"
        ? "w-full sm:w-[60%] lg:w-[40%] justify-center"
        : "w-full sm:w-[80%] lg:w-[60%]"
        }`}>
        {/* Close Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          className="absolute right-4 sm:right-6 lg:right-auto lg:-left-14 top-4 sm:top-6 lg:top-8 w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.15)] hover:bg-[#402B69] hover:text-white transition-all z-[70] group"
          aria-label="Close"
        >
          <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
        </motion.button>

        {/* Scrollable Container */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto custom-scrollbar">
          {/* Left Side - Stylists */}
          {quickPaymentType === "stylist" && (
            <div className="w-full lg:w-[38%] relative p-4 sm:p-6 lg:p-6 flex flex-col border-b lg:border-b-0 border-gray-100">
              <h2 className="text-[18px] sm:text-[20px] font-semibold text-[#402B69] pt-8 lg:pt-0 mb-4">
                Select Stylist
              </h2>

              {/* Search */}
              <div className="relative mb-6">
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full border border-[#E5E0EB] rounded-md py-2.5 pl-4 pr-10 text-[#402B69] text-[15px] font-medium focus:outline-none"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#885ABB]" />
              </div>

              {/* List */}
              <div className="space-y-3 overflow-y-auto flex-1 pr-2">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#885ABB]"></div>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    No employees found
                  </div>
                ) : (
                  filtered.map((s) => {
                    const isSelected = selectedId === s.id;
                    return (
                      <div
                        key={s.id}
                        onClick={() => setSelectedId(s.id)}
                        className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition
                        ${isSelected
                            ? "border-[#885ABB] bg-[#F9F9F9] shadow-sm"
                            : "border-[#E5E0EB] hover:bg-gray-50"
                          }
                      `}
                      >
                        <div
                          className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all duration-200
                          ${isSelected
                              ? `${s.darkColor} text-white ring-4 ring-white`
                              : `${s.lightColor} text-[#402B69]`
                            }
                        `}
                        >
                          {s.initials}
                        </div>

                        <span
                          className={`text-[15px] transition-colors duration-200
                          ${isSelected
                              ? "font-semibold text-black"
                              : "text-[#402B69]"
                            }
                        `}
                        >
                          {s.name}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="hidden lg:block absolute top-0 right-0 h-full w-[2px] bg-gray-200 shadow-[4px_0_12px_rgba(0,0,0,0.15)]"></div>
            </div>
          )}

          {/* Right Side - Amount */}
          <div className={`flex flex-col items-center justify-center p-6 sm:p-10 flex-shrink-0 ${quickPaymentType === "direct" ? "w-full" : "w-full lg:w-[62%]"
            }`}>
            <div className="w-full max-w-md flex flex-col items-center">
              <h3 className="text-[16px] font-medium text-[#402B69] mb-2">
                Enter Amount
              </h3>
              <div className="mb-2">
                <div className="text-[48px] font-bold text-[#402B69] text-center">
                  {user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || ""} {parseFloat(amount).toFixed(2)}
                </div>
              </div>

              {/* Calculator Section */}
              <div className="flex justify-center mb-6 lg:mb-8">
                <div className="flex flex-col lg:flex-row gap-4 px-2">
                  {/* Numeric Keypad */}
                  <div className="grid grid-cols-3 gap-2 flex-shrink-0">
                    {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "del"].map(
                      (k) => (
                        <button
                          key={k}
                          onClick={() => handleKey(k)}
                          className={
                            k === "del"
                              ? "h-14 w-16 sm:w-20 bg-red-50 border border-red-100 text-red-500 rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all active:scale-95 shadow-sm group"
                              : "h-14 w-16 sm:w-20 bg-white border border-[#885ABB] rounded-xl text-2xl font-bold text-[#885ABB] transition-all active:scale-95 shadow-sm"
                          }
                        >
                          {k === "del" ? <X size={22} strokeWidth={3} /> : k}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 w-full max-w-[280px]">
                <button
                  onClick={async () => {
                    await handlePayment("cash");
                  }}
                  disabled={submitting || parseFloat(amount) <= 0}
                  className="flex-1 h-[64px] bg-[#22C55E] text-white rounded-lg py-3 font-extrabold hover:bg-[#16a34a] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "..." : "CASH"}
                </button>
                <button
                  onClick={async () => {
                    await handlePayment("card");
                  }}
                  disabled={submitting || parseFloat(amount) <= 0}
                  className="flex-1 h-[64px] bg-[#885ABB] text-white rounded-lg py-3 font-extrabold hover:bg-[#6f3fa8] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "..." : "CARD"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaymentCompletedModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onClose();
        }}
        apiResponse={apiResponse}
        totalTendered={parseFloat(amount)}
        changeDue={0}
        bookingId={completedBookingId}
        onPaymentComplete={(id) => {
          if (onPaymentComplete) {
            onPaymentComplete(id);
          }
        }}
        isQuickPayment={true}
        quickPaymentData={{
          amount: parseFloat(amount),
          paymentType: lastPaymentType,
          stylistName: stylists.find(s => s.id === selectedId)?.name
        }}
      />
    </div>
  );
}
